import type { RecentDealListItemVehicle } from '../types';
import { MULTIPLE_TRADE_INS_LABEL, NO_TRADE_IN_LABEL, ELAPSED_TIME_LABELS } from '../constants';

/**
 * Returns a human-readable string representing the elapsed time since the given date (e.g., '5m ago').
 * @param date - The date to compare to now
 * @returns Elapsed time string
 */
export const getElapsedTime = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return diffInSeconds === 1 ? `1${ELAPSED_TIME_LABELS.SECOND}` : `${diffInSeconds}${ELAPSED_TIME_LABELS.SECOND}`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return diffInMinutes === 1 ? `1${ELAPSED_TIME_LABELS.MINUTE}` : `${diffInMinutes}${ELAPSED_TIME_LABELS.MINUTE}`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return diffInHours === 1 ? `1${ELAPSED_TIME_LABELS.HOUR}` : `${diffInHours}${ELAPSED_TIME_LABELS.HOUR}`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return diffInDays === 1 ? `1${ELAPSED_TIME_LABELS.DAY}` : `${diffInDays}${ELAPSED_TIME_LABELS.DAY}`;
};

/**
 * Formats a string describing when an item was last updated, including the source application
 * and optionally the user who made the change.
 *
 * @param lastUpdatedDate - The date and time when the item was last updated.
 * @param changedByApp - The name of the application that made the change.
 * @param changedByUser - (Optional) The name of the user who made the change.
 * @returns A formatted string indicating the elapsed time since the last update, the application,
 *          and optionally the user who made the change.
 */
export const formatLastUpdated = (lastUpdatedDate: Date, changedByApp: string, changedByUser?: string): string => {
    const elapsed = getElapsedTime(new Date(lastUpdatedDate));

    if (changedByUser) {
        return `${elapsed} from ${changedByApp} via ${changedByUser}`;
    }

    return `${elapsed} from ${changedByApp}`;
};

/**
 * Returns a label for the trade-in vehicles on a recent deal
 * @param tradeInVehicle - Array of trade-in vehicles
 * @returns Label string for trade-in vehicles
 */
export function getTradeInLabel(tradeInVehicle: RecentDealListItemVehicle[]): string {
    if (tradeInVehicle.length > 1) {
        return MULTIPLE_TRADE_INS_LABEL;
    }
    if (tradeInVehicle.length === 1) {
        return tradeInVehicle[0].name;
    }
    return NO_TRADE_IN_LABEL;
}
